'use client'

import { MdAdd, MdDelete } from 'react-icons/md'

/**
 * Um medicamento em edição.
 *
 * `quantity` é STRING porque é o que o `<input type="number">` devolve: o campo vazio
 * é `''`, e converter cedo demais transformaria "ainda não digitei" em zero. Quem
 * converte é o `submit` de cada tela.
 */
export interface MedicationItem {
    medication: string
    dosage: string
    frequency: string
    duration: string
    instructions: string
    quantity: string
}

export const emptyMedication: MedicationItem = {
    medication: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: '',
    quantity: '',
}

interface Props {
    value: MedicationItem[]
    onChange: (medications: MedicationItem[]) => void
    /** Some no modelo, onde nenhum medicamento ainda é o estado inicial esperado. */
    emptyMessage?: string
}

/**
 * Lista de medicamentos da receita.
 *
 * O mesmo bloco serve à receita do paciente e ao MODELO de receita: é a mesma
 * informação — nome, dose, frequência, duração, quantidade e orientação —, e o modelo
 * existe justamente para que o médico não redigite isto a cada paciente. Duas cópias
 * do formulário divergiriam no primeiro campo novo, e o campo que faltasse no modelo
 * seria descoberto só na hora de aplicar.
 */
export default function MedicationListEditor({
    value,
    onChange,
    emptyMessage = 'Nenhum medicamento adicionado.',
}: Props) {
    const updateMedication = (
        index: number,
        field: keyof MedicationItem,
        fieldValue: string
    ) => {
        onChange(
            value.map((item, i) =>
                i === index ? { ...item, [field]: fieldValue } : item
            )
        )
    }

    const addMedication = () => {
        onChange([...value, { ...emptyMedication }])
    }

    const removeMedication = (index: number) => {
        onChange(value.filter((_, i) => i !== index))
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <span className="label-text font-medium">Medicamentos</span>
                <button
                    type="button"
                    onClick={addMedication}
                    className="btn btn-xs btn-secondary gap-1"
                >
                    <MdAdd className="text-base" />
                    Adicionar
                </button>
            </div>

            {value.length === 0 ? (
                <p className="text-sm text-base-content/50 text-center py-4">
                    {emptyMessage}
                </p>
            ) : (
                <div className="space-y-3">
                    {value.map((med, index) => (
                        <div
                            key={index}
                            className="bg-base-200/50 rounded-lg p-3 border border-base-300/50"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-base-content/60">
                                    Medicamento {index + 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeMedication(index)}
                                    className="btn btn-ghost btn-xs btn-square text-error"
                                    title="Remover"
                                >
                                    <MdDelete className="text-base" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text text-xs">
                                            Medicamento
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Nome do medicamento"
                                        value={med.medication}
                                        onChange={event =>
                                            updateMedication(
                                                index,
                                                'medication',
                                                event.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text text-xs">
                                            Dosagem
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Ex: 500mg"
                                        value={med.dosage}
                                        onChange={event =>
                                            updateMedication(
                                                index,
                                                'dosage',
                                                event.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text text-xs">
                                            Frequência
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Ex: 8 em 8 horas"
                                        value={med.frequency}
                                        onChange={event =>
                                            updateMedication(
                                                index,
                                                'frequency',
                                                event.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text text-xs">
                                            Duração
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Ex: 7 dias"
                                        value={med.duration}
                                        onChange={event =>
                                            updateMedication(
                                                index,
                                                'duration',
                                                event.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text text-xs">
                                            Quantidade
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Ex: 2"
                                        value={med.quantity}
                                        onChange={event =>
                                            updateMedication(
                                                index,
                                                'quantity',
                                                event.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text text-xs">
                                            Instruções
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        placeholder="Ex: após as refeições"
                                        value={med.instructions}
                                        onChange={event =>
                                            updateMedication(
                                                index,
                                                'instructions',
                                                event.target.value
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
